/**
 * Data layer for `conversion-pull-endpoint`.
 *
 * Thin typed fetch wrappers over the `/api/v1/conversion-pull-endpoint` BE.
 *
 *   GET  /conversions?since&until&status&offer_id&page&per_page&cursor
 *   POST /seed      (operator/test: inject a conversion row)
 *   GET  /stats     (per-status counts)
 *
 * Raw `fetch()` is confined to this file per the dash contract (D6). All
 * requests go out with `x-tenant-id: t1` in dev.
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE = "/api/v1/conversion-pull-endpoint";
const TENANT_ID = "t1";

export const CONVERSION_STATUSES = ["pending", "approved", "rejected", "paid"] as const;
export type ConversionStatus = (typeof CONVERSION_STATUSES)[number];

export const STATUS_LABELS: Record<ConversionStatus, string> = {
	pending: "Pending",
	approved: "Approved",
	rejected: "Rejected",
	paid: "Paid",
};

// ---------------------------------------------------------------------------
// Types / schemas
// ---------------------------------------------------------------------------

export interface ConversionRow {
	id: string;
	click_id: string;
	offer_id: string;
	event: string;
	payout: number;
	currency: string;
	status: ConversionStatus;
	tx_id: string | null;
	created_at: number;
}

export interface ConversionsMeta {
	page: number | null;
	per_page: number;
	total: number;
	total_pages: number;
	next_cursor: string | null;
}

export interface ListConversionsResponse {
	data: ConversionRow[];
	meta: ConversionsMeta;
}

export interface StatsResponse {
	total: number;
	by_status: Record<ConversionStatus, number>;
}

export interface ListConversionsParams {
	since?: number | null;
	until?: number | null;
	status?: ConversionStatus[];
	offer_id?: string | null;
	page?: number;
	per_page?: number;
	cursor?: string | null;
}

// Zod schema for the create (seed) form. RHF binds to this shape.
export const createConversionSchema = z.object({
	click_id: z.string().trim().min(1, "Required").max(256),
	offer_id: z.string().trim().min(1, "Required").max(120),
	event: z.string().trim().min(1).max(64).default("conversion"),
	payout: z
		.union([z.string(), z.number()])
		.transform((v) => (typeof v === "number" ? v : Number.parseFloat(v)))
		.refine((n) => Number.isFinite(n) && n >= 0, { message: "Must be >= 0" }),
	currency: z.string().trim().min(1).max(16).default("USD"),
	status: z.enum(CONVERSION_STATUSES).default("pending"),
	tx_id: z.string().trim().max(256).optional().or(z.literal("")),
});

export type CreateConversionForm = z.input<typeof createConversionSchema>;
export type CreateConversionBody = z.output<typeof createConversionSchema>;

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

function headers(extra?: Record<string, string>): Record<string, string> {
	return {
		accept: "application/json",
		"x-tenant-id": TENANT_ID,
		...(extra ?? {}),
	};
}

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildQuery(p: ListConversionsParams | undefined): string {
	if (!p) return "";
	const q = new URLSearchParams();
	if (p.since != null) q.set("since", String(p.since));
	if (p.until != null) q.set("until", String(p.until));
	if (p.status && p.status.length > 0) q.set("status", p.status.join(","));
	if (p.offer_id) q.set("offer_id", p.offer_id);
	if (p.page != null) q.set("page", String(p.page));
	if (p.per_page != null) q.set("per_page", String(p.per_page));
	if (p.cursor) q.set("cursor", p.cursor);
	const s = q.toString();
	return s ? `?${s}` : "";
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function listConversions(
	params?: ListConversionsParams,
): Promise<ListConversionsResponse> {
	const res = await fetch(`${BASE}/conversions${buildQuery(params)}`, {
		method: "GET",
		credentials: "include",
		headers: headers(),
	});
	return parseOrThrow<ListConversionsResponse>(res);
}

export async function createConversion(
	body: CreateConversionBody,
): Promise<ConversionRow> {
	// `tx_id` comes through as "" from the empty form — the BE treats empty as
	// null, but we normalize here so the payload is minimal on the wire.
	const cleaned: Record<string, unknown> = { ...body };
	if (cleaned.tx_id === "" || cleaned.tx_id === undefined) delete cleaned.tx_id;
	const res = await fetch(`${BASE}/seed`, {
		method: "POST",
		credentials: "include",
		headers: headers({ "content-type": "application/json" }),
		body: JSON.stringify(cleaned),
	});
	return parseOrThrow<ConversionRow>(res);
}

export async function getStats(): Promise<StatsResponse> {
	const res = await fetch(`${BASE}/stats`, {
		method: "GET",
		credentials: "include",
		headers: headers(),
	});
	return parseOrThrow<StatsResponse>(res);
}

/**
 * Alias kept for forward-compatibility. The pull-endpoint BE does not expose
 * a DELETE route today; this throws if called.
 */
export async function removeConversion(_id: string): Promise<{ removed: boolean }> {
	throw new Error("delete_not_supported");
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const conversionPullEndpointQueryKeys = {
	all: ["conversion-pull-endpoint"] as const,
	list: (params?: ListConversionsParams) =>
		[
			...["conversion-pull-endpoint"],
			"list",
			params ?? {},
		] as const,
	stats: () =>
		[...["conversion-pull-endpoint"], "stats"] as const,
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export function statusBadgeClass(status: ConversionStatus): string {
	switch (status) {
		case "approved":
			return "bg-green-50 text-green-700 border-green-200";
		case "paid":
			return "bg-green-100 text-green-800 border-green-300";
		case "rejected":
			return "bg-red-50 text-red-700 border-red-200";
		case "pending":
		default:
			return "bg-zinc-100 text-zinc-700 border-zinc-200";
	}
}

export function formatTimestamp(unixSeconds: number): string {
	if (!Number.isFinite(unixSeconds)) return "—";
	try {
		return new Date(unixSeconds * 1000).toISOString().replace("T", " ").slice(0, 19);
	} catch {
		return String(unixSeconds);
	}
}

export function formatAmount(payout: number, currency: string): string {
	if (!Number.isFinite(payout)) return `${currency} 0.00`;
	return `${currency} ${payout.toFixed(2)}`;
}
